// src/utils/ttmArucoCalibrate.js
// Client-side calibration from a printed ArUco fiducial marker.
//
// WHY THIS EXISTS: jsQR scored ~0/N on real field photos of the AI Stick
// (angled, room-lit, dense URL-bearing QR small in frame). ArUco markers carry
// far fewer bits (big cells), so they decode at smaller pixel sizes, lower
// contrast, and steeper angles than a QR. This module is the test bed: prove
// ArUco decodes on the EXACT shots where the QR couldn't, before wiring it into
// the calibration ladder.
//
// Mirrors the shape of ttmQrCalibrate.js on purpose so the eventual integration
// is a parallel tier, not a rewrite.
//
// Requires: npm install js-aruco2
//
// IMPORT NOTE: js-aruco2 v2 ships as plain global-defining scripts (its
// package.json "main" is src/aruco.js with NO ES module exports). So a normal
// `import { AR } from "js-aruco2"` resolves to nothing. Instead we import the
// source files directly, in dependency order, so they execute and define the
// global `window.AR`. aruco.js needs cv.js; svd.js is pulled for completeness.

// aruco.js sets window.AR when it runs. Read it back here.
const AR = typeof window !== "undefined" ? window.AR : undefined;

// Physical marker size, black border to black border, on a 100%-scale print.
// THIS is the single most scale-critical constant for the ArUco path — measure
// it on your actual printed stick and update it. Placeholder = 2 inches to match
// the current QR footprint; you may print the ArUco tag larger for robustness.
export const ARUCO_WIDTH_INCHES = 3.432;

// Default dictionary. ARUCO_MIP_36h12 (8x8, 36-bit) is the js-aruco2 default and
// a good robust choice. The plain "ARUCO" dict (7x7, 25-bit) has even bigger
// cells per unit area and may decode at smaller sizes — worth A/B testing.
const DEFAULT_DICTIONARY = "ARUCO_MIP_36h12";

// One detector instance, reused. Created lazily so an import-time AR resolution
// failure surfaces as a clear thrown error instead of a confusing crash.
let _detector = null;
let _detectorDict = null;
function getDetector(dictionaryName, maxHammingDistance) {
  if (!AR || !AR.Detector) {
    throw new Error(
      "js-aruco2 AR namespace not found. The direct source imports at the top " +
        "of ttmArucoCalibrate.js did not define window.AR — check that " +
        "node_modules/js-aruco2/src/{cv,svd,aruco}.js exist and the build " +
        "included them."
    );
  }
  if (!_detector || _detectorDict !== dictionaryName) {
    const cfg = { dictionaryName };
    if (typeof maxHammingDistance === "number") cfg.maxHammingDistance = maxHammingDistance;
    _detector = new AR.Detector(cfg);
    _detectorDict = dictionaryName;
  }
  return _detector;
}

/**
 * Detect a calibration ArUco marker and derive scale from its known size.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} image  decoded source image
 * @param {object} opts
 *   markerInches    real-world marker width  (default ARUCO_WIDTH_INCHES)
 *   dictionaryName  ArUco dictionary         (default ARUCO_MIP_36h12)
 *   maxHammingDistance  detector tolerance   (default: dictionary default)
 *   maxDetectWidth  downscale cap for speed  (default 1600 — see note)
 *   minMarkerPx     below this -> lowConfidence (default 60)
 *   expectId        if set (number), only accept a marker with this id, so a
 *                   stray ArUco tag in frame can't hijack scale. STRONGLY
 *                   recommended for production once you pick your marker id.
 *
 * @returns null if no (matching) marker is found, otherwise:
 *   {
 *     pxPerInch,            // scale in ORIGINAL image pixels per inch
 *     markerPixelWidth,     // mean side length of the marker, original pixels
 *     corners: { tl, tr, br, bl },  // original image-pixel coordinates
 *     coverage,             // markerPixelWidth / imageWidth (0..1)
 *     markerId,             // detected marker id
 *     lowConfidence,        // true if the marker is small in frame
 *   }
 */
export function detectArucoCalibration(image, opts = {}) {
  const markerInches = opts.markerInches || ARUCO_WIDTH_INCHES;
  const dictionaryName = opts.dictionaryName || DEFAULT_DICTIONARY;
  // NOTE ON maxDetectWidth: the QR path's 1400 cap was shrinking the QR below
  // jsQR's decode floor. ArUco tolerates small markers far better, but the same
  // trap applies — keep this generous (or test removing it) so you don't
  // re-create the QR mistake. 1600 is a starting point; for the FIELD TEST,
  // pass { maxDetectWidth: <natural width> } to feed full res, just like we did
  // to disprove the QR cap theory.
  const maxDetectWidth = opts.maxDetectWidth || 1600;
  const minMarkerPx = opts.minMarkerPx || 60;

  const natW = image.naturalWidth || image.width;
  const natH = image.naturalHeight || image.height;
  if (!natW || !natH) return null;

  // Downscale for speed; corners are rescaled back to original pixels afterward.
  const scale = Math.min(1, maxDetectWidth / natW);
  const w = Math.max(1, Math.round(natW * scale));
  const h = Math.max(1, Math.round(natH * scale));

  let imageData;
  try {
    const cv = document.createElement("canvas");
    cv.width = w;
    cv.height = h;
    const ctx = cv.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, w, h);
    imageData = ctx.getImageData(0, 0, w, h);
  } catch (e) {
    // Tainted canvas / unsupported source
    return null;
  }

  const detector = getDetector(dictionaryName, opts.maxHammingDistance);

  // js-aruco2 accepts an ImageData object directly.
  const markers = detector.detect(imageData);
  if (!markers || markers.length === 0) return null;

  // Pick the marker to use. If expectId is set, require it (guard against stray
  // tags). Otherwise take the largest marker in frame (most pixels = best scale).
  let marker = null;
  if (opts.expectId != null) {
    marker = markers.find((m) => m.id === opts.expectId) || null;
    if (!marker) return null; // a marker, but not ours
  } else {
    marker = markers.reduce((best, m) => {
      const a = quadMeanSide(m.corners);
      const b = best ? quadMeanSide(best.corners) : -1;
      return a > b ? m : best;
    }, null);
  }
  if (!marker || !marker.corners || marker.corners.length < 4) return null;

  // Rescale corners from detection space back to original image pixels.
  const inv = 1 / scale;
  const P = (p) => ({ x: p.x * inv, y: p.y * inv });
  // js-aruco2 returns corners in order; map to named corners for readability.
  // (Order is consistent per the library; tl/tr/br/bl labels are nominal — the
  //  mean side length below is order-independent, so scale is unaffected even if
  //  the rotation differs. The named corners are for the future homography step.)
  const c = marker.corners.map(P);
  const tl = c[0];
  const tr = c[1];
  const br = c[2];
  const bl = c[3];

  const markerPixelWidth = quadMeanSide([tl, tr, br, bl]);
  if (!markerPixelWidth || !isFinite(markerPixelWidth) || markerPixelWidth < 8) {
    return null;
  }

  return {
    pxPerInch: markerPixelWidth / markerInches,
    markerPixelWidth,
    corners: { tl, tr, br, bl },
    coverage: markerPixelWidth / natW,
    markerId: marker.id,
    lowConfidence: markerPixelWidth < minMarkerPx,
  };
}

// Mean of the four side lengths of a quad given as [tl, tr, br, bl].
// Order-independent for a closed quad, so robust to corner rotation.
function quadMeanSide(corners) {
  if (!corners || corners.length < 4) return 0;
  const d = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  const [p0, p1, p2, p3] = corners;
  return (d(p0, p1) + d(p1, p2) + d(p2, p3) + d(p3, p0)) / 4;
}
